import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';
import styled from 'styled-components';

import axios from '../../../services/axios';
import Modal from '../../../components/Modal/Modal';
import { FormGrid } from '../../../styles/layout/FormGrid';
import { Field } from '../../../styles/forms/Field';
import { TableWrapper } from '../../../styles/table/TableWrapper';
import { Table } from '../../../styles/table/Table';

// =============================================================================
// Estilos
// =============================================================================

const StatusPill = styled.span`
   display: inline-flex;
   align-items: center;
   justify-content: center;
   padding: 3px 10px;
   border-radius: 999px;
   font-size: 11px;
   font-weight: 700;
   border: 1px solid ${({ $borderColor }) => $borderColor ?? 'rgba(0,0,0,0.15)'};
   background: ${({ $backgroundColor }) =>
      $backgroundColor ?? 'rgba(0,0,0,0.06)'};
   color: ${({ $textColor }) => $textColor ?? '#444'};
`;

const GroupHeader = styled.div`
   display: flex;
   flex-wrap: wrap;
   gap: 16px;
   padding: 12px 0 16px;
   border-bottom: 1px solid rgba(0, 0, 0, 0.07);
   margin-bottom: 16px;
`;

const GroupHeaderItem = styled.div`
   display: flex;
   flex-direction: column;
   gap: 2px;
`;

const GroupHeaderLabel = styled.span`
   font-size: 0.7rem;
   color: #9ca3af;
   text-transform: uppercase;
   letter-spacing: 0.03em;
`;

const GroupHeaderValue = styled.span`
   font-size: 0.875rem;
   font-weight: 500;
   color: #111827;
`;

// Wrapper com scroll interno — impede que o dialog cresça além da viewport
// e garante que o header do modal fique sempre visível no topo.
const ScrollableBody = styled.div`
   max-height: calc(100dvh - 180px);
   overflow-y: auto;
   overflow-x: hidden;

   /* fallback para navegadores sem dvh */
   @supports not (height: 100dvh) {
      max-height: calc(100vh - 180px);
   }
`;

// =============================================================================
// Constantes de configuração de status
// =============================================================================

const PARENT_STATUS_CONFIG = {
   ABERTO: {
      label: 'Aberto',
      bg: 'rgba(2,136,209,0.10)',
      border: 'rgba(2,136,209,0.30)',
      text: '#01579b',
   },
   QUITADO: {
      label: 'Quitado',
      bg: 'rgba(46,125,50,0.12)',
      border: 'rgba(46,125,50,0.35)',
      text: '#1b5e20',
   },
   CANCELADO: {
      label: 'Cancelado',
      bg: 'rgba(0,0,0,0.06)',
      border: 'rgba(0,0,0,0.15)',
      text: '#444',
   },
   CANCELADO_PARCIAL: {
      label: 'Cancelado parcial',
      bg: 'rgba(230,81,0,0.10)',
      border: 'rgba(230,81,0,0.30)',
      text: '#bf360c',
   },
   CONCLUIDO_PARCIAL: {
      label: 'Concluído parcial',
      bg: 'rgba(123,31,162,0.10)',
      border: 'rgba(123,31,162,0.30)',
      text: '#4a148c',
   },
};

const INSTALLMENT_STATUS_CONFIG = {
   PENDENTE: {
      label: 'Pendente',
      bg: 'rgba(255,193,7,0.15)',
      border: 'rgba(255,193,7,0.50)',
      text: '#7d5a00',
   },
   PAGO: {
      label: 'Pago',
      bg: 'rgba(46,125,50,0.12)',
      border: 'rgba(46,125,50,0.35)',
      text: '#1b5e20',
   },
   CANCELADO: {
      label: 'Cancelado',
      bg: 'rgba(0,0,0,0.06)',
      border: 'rgba(0,0,0,0.15)',
      text: '#444',
   },
};

// =============================================================================
// Helpers
// =============================================================================

function formatDateBR(isoDateString) {
   if (!isoDateString) return '—';
   const [year, month, day] = String(isoDateString).substring(0, 10).split('-');
   return `${day}/${month}/${year}`;
}

function formatMonthBR(isoDateString) {
   if (!isoDateString) return '—';
   const [year, month] = String(isoDateString).substring(0, 10).split('-');
   return `${month}/${year}`;
}

function formatCurrencyBRL(value) {
   if (value === null || value === undefined) return '—';
   return Number(value).toLocaleString('pt-BR', {
      style: 'currency',
      currency: 'BRL',
   });
}

function StatusBadge({ statusValue, config }) {
   const statusConfig = config[statusValue] ?? {
      label: statusValue,
      bg: null,
      border: null,
      text: null,
   };
   return (
      <StatusPill
         $backgroundColor={statusConfig.bg}
         $borderColor={statusConfig.border}
         $textColor={statusConfig.text}
      >
         {statusConfig.label}
      </StatusPill>
   );
}

// =============================================================================
// Estados iniciais dos formulários
// =============================================================================

const EMPTY_INSTALLMENT_UPDATE_STATE = {
   installment_status: '',
   payment_date: '',
   cancellation_reason: '',
   reference_month: '',
   notes: '',
};

const EMPTY_CANCELLATION_STATE = {
   cancellation_reason: '',
   cancellation_notes: '',
};

// =============================================================================
// Componente principal
// =============================================================================

/**
 * Modal que carrega e exibe o grupo pai de um débito (adiantamento ou compra),
 * com ações de cancelar o grupo e atualizar parcelas individuais.
 *
 * Props:
 *   isOpen       {boolean}   — controla visibilidade
 *   onClose      {fn}        — chamado ao fechar
 *   onSuccess    {fn}        — chamado após qualquer operação bem-sucedida
 *   debtType     {string}    — 'ADIANTAMENTO' | 'COMPRA_EM_FOLHA'
 *   employeeId   {number}    — ID do funcionário dono do débito
 *   employeeName {string}    — nome do funcionário (para exibição no título)
 *   groupId      {number}    — ID do grupo pai (advance.id ou purchase.id)
 */
export default function DebtGroupDetailModal({
   isOpen,
   onClose,
   onSuccess,
   debtType,
   employeeId,
   employeeName,
   groupId,
}) {
   const [groupData, setGroupData] = useState(null);
   const [isLoadingGroup, setIsLoadingGroup] = useState(false);

   // ─── Modal de atualização de parcela ──────────────────────────
   const [installmentBeingUpdated, setInstallmentBeingUpdated] = useState(null);
   const [installmentUpdateFormData, setInstallmentUpdateFormData] = useState(
      EMPTY_INSTALLMENT_UPDATE_STATE
   );
   const [isSubmittingInstallmentUpdate, setIsSubmittingInstallmentUpdate] =
      useState(false);

   // ─── Modal de cancelamento do grupo ───────────────────────────
   const [isCancellationModalOpen, setIsCancellationModalOpen] =
      useState(false);
   const [cancellationFormData, setCancellationFormData] = useState(
      EMPTY_CANCELLATION_STATE
   );
   const [isSubmittingCancellation, setIsSubmittingCancellation] =
      useState(false);

   // ─── Endpoint base conforme o tipo ────────────────────────────
   const baseEndpoint =
      debtType === 'ADIANTAMENTO'
         ? `/hr/employees/${employeeId}/salary-advances/${groupId}`
         : `/hr/employees/${employeeId}/purchase-deductions/${groupId}`;

   // ─── Carregar grupo pai ────────────────────────────────────────
   const fetchGroupData = useCallback(async () => {
      if (!isOpen || !employeeId || !groupId) return;
      setIsLoadingGroup(true);
      try {
         const response = await axios.get(baseEndpoint);
         const dataKey =
            debtType === 'ADIANTAMENTO'
               ? 'salary_advance'
               : 'purchase_deduction';
         setGroupData(response.data?.data?.[dataKey] ?? null);
      } catch (error) {
         const errorMessage =
            error?.response?.data?.errors?.[0] ??
            error?.message ??
            'Erro ao carregar dados do débito.';
         toast.error(errorMessage);
      } finally {
         setIsLoadingGroup(false);
      }
   }, [isOpen, employeeId, groupId, baseEndpoint, debtType]);

   useEffect(() => {
      fetchGroupData();
   }, [fetchGroupData]);

   // ─── Fechar modal principal ────────────────────────────────────
   const handleClose = useCallback(() => {
      setGroupData(null);
      setInstallmentBeingUpdated(null);
      setIsCancellationModalOpen(false);
      onClose();
   }, [onClose]);

   // ─── Atualizar parcela ─────────────────────────────────────────
   const handleOpenInstallmentUpdateModal = (installment) => {
      setInstallmentBeingUpdated(installment);
      setInstallmentUpdateFormData({
         installment_status: installment.installment_status,
         payment_date: installment.payment_date?.substring(0, 10) ?? '',
         cancellation_reason: '',
         reference_month: installment.reference_month?.substring(0, 7) ?? '',
         notes: installment.notes ?? '',
      });
   };

   const handleCloseInstallmentUpdateModal = useCallback(() => {
      setInstallmentBeingUpdated(null);
   }, []);

   const handleInstallmentUpdateFieldChange = (event) => {
      const { name, value } = event.target;
      setInstallmentUpdateFormData((prev) => ({ ...prev, [name]: value }));
   };

   const handleInstallmentUpdateSubmit = async () => {
      if (!installmentBeingUpdated) return;

      if (
         installmentUpdateFormData.installment_status === 'PAGO' &&
         !installmentUpdateFormData.payment_date
      ) {
         toast.error('Data de pagamento é obrigatória ao marcar como PAGO.');
         return;
      }
      if (
         installmentUpdateFormData.installment_status === 'CANCELADO' &&
         !installmentUpdateFormData.cancellation_reason
      ) {
         toast.error('Motivo do cancelamento é obrigatório.');
         return;
      }

      const installmentEndpoint =
         debtType === 'ADIANTAMENTO'
            ? `${baseEndpoint}/installments/${installmentBeingUpdated.id}`
            : `${baseEndpoint}/installments/${installmentBeingUpdated.id}`;

      setIsSubmittingInstallmentUpdate(true);
      try {
         await axios.patch(installmentEndpoint, {
            installment_status: installmentUpdateFormData.installment_status,
            payment_date: installmentUpdateFormData.payment_date || null,
            cancellation_reason:
               installmentUpdateFormData.cancellation_reason || null,
            reference_month: installmentUpdateFormData.reference_month
               ? `${installmentUpdateFormData.reference_month}-01`
               : null,
            notes: installmentUpdateFormData.notes || null,
         });

         toast.success('Parcela atualizada com sucesso!');
         setInstallmentBeingUpdated(null);
         fetchGroupData();
         onSuccess?.();
      } catch (error) {
         const errorMessage =
            error?.response?.data?.errors?.[0] ??
            error?.response?.data?.message ??
            error?.message ??
            'Erro ao atualizar parcela.';
         toast.error(errorMessage);
      } finally {
         setIsSubmittingInstallmentUpdate(false);
      }
   };

   // ─── Cancelar grupo pai ────────────────────────────────────────
   const handleOpenCancellationModal = () => {
      setCancellationFormData(EMPTY_CANCELLATION_STATE);
      setIsCancellationModalOpen(true);
   };

   const handleCloseCancellationModal = useCallback(() => {
      setIsCancellationModalOpen(false);
   }, []);

   const handleCancellationFieldChange = (event) => {
      const { name, value } = event.target;
      setCancellationFormData((prev) => ({ ...prev, [name]: value }));
   };

   const handleCancellationSubmit = async () => {
      if (!cancellationFormData.cancellation_reason) {
         toast.error('Motivo do cancelamento é obrigatório.');
         return;
      }

      setIsSubmittingCancellation(true);
      try {
         await axios.patch(`${baseEndpoint}/cancel`, cancellationFormData);
         toast.success('Débito cancelado com sucesso.');
         setIsCancellationModalOpen(false);
         fetchGroupData();
         onSuccess?.();
      } catch (error) {
         const errorMessage =
            error?.response?.data?.errors?.[0] ??
            error?.response?.data?.message ??
            error?.message ??
            'Erro ao cancelar débito.';
         toast.error(errorMessage);
      } finally {
         setIsSubmittingCancellation(false);
      }
   };

   // ─── Dados do grupo por tipo ───────────────────────────────────
   const isAdvance = debtType === 'ADIANTAMENTO';
   const parentStatus = isAdvance
      ? groupData?.advance_status
      : groupData?.purchase_status;
   const description = isAdvance
      ? groupData?.description
      : groupData?.product_description;
   const totalAmount = isAdvance
      ? groupData?.total_amount
      : groupData?.total_purchase_amount;
   const isCancelled = parentStatus === 'CANCELADO';

   const modalTitle = isAdvance
      ? `Adiantamento — ${employeeName ?? ''}`
      : `Compra em Folha — ${employeeName ?? ''}`;

   // ─── Render ───────────────────────────────────────────────────
   return (
      <>
         <Modal
            isOpen={isOpen}
            title={modalTitle}
            onClose={handleClose}
            maxWidth="700px"
         >
            <ScrollableBody>
               {isLoadingGroup && (
                  <p style={{ fontSize: '13px', color: '#888' }}>
                     Carregando...
                  </p>
               )}

               {!isLoadingGroup && groupData && (
                  <>
                     {/* ─── Cabeçalho do grupo ─── */}
                     <GroupHeader>
                        <GroupHeaderItem>
                           <GroupHeaderLabel>Descrição</GroupHeaderLabel>
                           <GroupHeaderValue>{description}</GroupHeaderValue>
                        </GroupHeaderItem>

                        <GroupHeaderItem>
                           <GroupHeaderLabel>Valor total</GroupHeaderLabel>
                           <GroupHeaderValue>
                              {formatCurrencyBRL(totalAmount)}
                           </GroupHeaderValue>
                        </GroupHeaderItem>

                        <GroupHeaderItem>
                           <GroupHeaderLabel>Parcelas</GroupHeaderLabel>
                           <GroupHeaderValue>
                              {groupData.total_installments_count}x
                           </GroupHeaderValue>
                        </GroupHeaderItem>

                        {isAdvance && groupData.request_date && (
                           <GroupHeaderItem>
                              <GroupHeaderLabel>
                                 Data solicitação
                              </GroupHeaderLabel>
                              <GroupHeaderValue>
                                 {formatDateBR(groupData.request_date)}
                              </GroupHeaderValue>
                           </GroupHeaderItem>
                        )}

                        {!isAdvance && groupData.purchase_date && (
                           <GroupHeaderItem>
                              <GroupHeaderLabel>
                                 Data da compra
                              </GroupHeaderLabel>
                              <GroupHeaderValue>
                                 {formatDateBR(groupData.purchase_date)}
                              </GroupHeaderValue>
                           </GroupHeaderItem>
                        )}

                        {!isAdvance && groupData.order_number && (
                           <GroupHeaderItem>
                              <GroupHeaderLabel>Nº pedido</GroupHeaderLabel>
                              <GroupHeaderValue>
                                 {groupData.order_number}
                              </GroupHeaderValue>
                           </GroupHeaderItem>
                        )}

                        <GroupHeaderItem>
                           <GroupHeaderLabel>Status</GroupHeaderLabel>
                           <StatusBadge
                              statusValue={parentStatus}
                              config={PARENT_STATUS_CONFIG}
                           />
                        </GroupHeaderItem>

                        {/* ─── Botão cancelar grupo ─── */}
                        {!isCancelled && (
                           <div
                              style={{
                                 marginLeft: 'auto',
                                 alignSelf: 'center',
                              }}
                              title={
                                 groupData.installments?.some(
                                    (i) => i.installment_status === 'PAGO'
                                 )
                                    ? 'Não é possível cancelar um grupo com parcelas já pagas'
                                    : undefined
                              }
                           >
                              <button
                                 type="button"
                                 onClick={handleOpenCancellationModal}
                                 disabled={groupData.installments?.some(
                                    (i) => i.installment_status === 'PAGO'
                                 )}
                              >
                                 Cancelar débito
                              </button>
                           </div>
                        )}
                     </GroupHeader>

                     {/* ─── Tabela de parcelas ─── */}
                     <TableWrapper $minWidth="480px">
                        <Table>
                           <thead>
                              <tr>
                                 <th style={{ textAlign: 'center' }}>
                                    Parcela
                                 </th>
                                 <th style={{ textAlign: 'center' }}>
                                    Mês ref.
                                 </th>
                                 <th style={{ textAlign: 'right' }}>Valor</th>
                                 <th>Status</th>
                                 <th style={{ textAlign: 'center' }}>Pgto.</th>
                                 <th> Ações</th>
                              </tr>
                           </thead>
                           <tbody>
                              {groupData.installments?.map((installment) => (
                                 <tr key={installment.id}>
                                    <td style={{ textAlign: 'center' }}>
                                       {installment.installment_number}/
                                       {groupData.total_installments_count}
                                    </td>
                                    <td style={{ textAlign: 'center' }}>
                                       {formatMonthBR(
                                          installment.reference_month
                                       )}
                                    </td>
                                    <td style={{ textAlign: 'right' }}>
                                       {formatCurrencyBRL(
                                          installment.installment_amount
                                       )}
                                    </td>
                                    <td>
                                       <StatusBadge
                                          statusValue={
                                             installment.installment_status
                                          }
                                          config={INSTALLMENT_STATUS_CONFIG}
                                       />
                                    </td>
                                    <td style={{ textAlign: 'center' }}>
                                       {formatDateBR(installment.payment_date)}
                                    </td>
                                    <td>
                                       {!isCancelled && (
                                          <button
                                             type="button"
                                             onClick={() =>
                                                handleOpenInstallmentUpdateModal(
                                                   installment
                                                )
                                             }
                                             style={{ fontSize: '0.75rem' }}
                                          >
                                             Editar
                                          </button>
                                       )}
                                    </td>
                                 </tr>
                              ))}
                           </tbody>
                        </Table>
                     </TableWrapper>
                  </>
               )}
            </ScrollableBody>
         </Modal>

         {/* ─── Modal: atualizar parcela ─────────────────────────────── */}
         <Modal
            isOpen={Boolean(installmentBeingUpdated)}
            title={`Parcela ${installmentBeingUpdated?.installment_number ?? ''}/${groupData?.total_installments_count ?? ''} — ${formatMonthBR(installmentBeingUpdated?.reference_month)}`}
            onClose={handleCloseInstallmentUpdateModal}
            maxWidth="440px"
         >
            <FormGrid $cols={12} $colsMobile={12}>
               <Field $col={12} $colMobile={12}>
                  <label htmlFor="detail-installment-status">Status *</label>
                  <select
                     id="detail-installment-status"
                     name="installment_status"
                     value={installmentUpdateFormData.installment_status}
                     onChange={handleInstallmentUpdateFieldChange}
                  >
                     <option value="PENDENTE">Pendente</option>
                     <option value="PAGO">Pago</option>
                     <option value="CANCELADO">Cancelado</option>
                  </select>
               </Field>

               <Field $col={12} $colMobile={12}>
                  <label htmlFor="detail-installment-reference-month">
                     Mês de referência
                  </label>
                  <input
                     id="detail-installment-reference-month"
                     type="month"
                     name="reference_month"
                     value={installmentUpdateFormData.reference_month}
                     onChange={handleInstallmentUpdateFieldChange}
                  />
               </Field>

               {installmentUpdateFormData.installment_status === 'PAGO' && (
                  <Field $col={12} $colMobile={12}>
                     <label htmlFor="detail-installment-payment-date">
                        Data de pagamento *
                     </label>
                     <input
                        id="detail-installment-payment-date"
                        type="date"
                        name="payment_date"
                        value={installmentUpdateFormData.payment_date}
                        onChange={handleInstallmentUpdateFieldChange}
                     />
                  </Field>
               )}

               {installmentUpdateFormData.installment_status ===
                  'CANCELADO' && (
                  <Field $col={12} $colMobile={12}>
                     <label htmlFor="detail-installment-cancellation-reason">
                        Motivo *
                     </label>
                     <select
                        id="detail-installment-cancellation-reason"
                        name="cancellation_reason"
                        value={installmentUpdateFormData.cancellation_reason}
                        onChange={handleInstallmentUpdateFieldChange}
                     >
                        <option value="">Selecione...</option>
                        <option value="CADASTRO_INCORRETO">
                           Cadastro incorreto
                        </option>
                        <option value="CANCELAMENTO_DE_DEBITO">
                           Cancelamento de débito
                        </option>
                     </select>
                  </Field>
               )}

               <Field $col={12} $colMobile={12}>
                  <label htmlFor="detail-installment-notes">Observações</label>
                  <input
                     id="detail-installment-notes"
                     type="text"
                     name="notes"
                     value={installmentUpdateFormData.notes}
                     onChange={handleInstallmentUpdateFieldChange}
                     placeholder="Observações opcionais..."
                  />
               </Field>

               <Field $col={12} $colMobile={12}>
                  <div
                     style={{
                        display: 'flex',
                        justifyContent: 'flex-end',
                        gap: '10px',
                        paddingTop: '4px',
                     }}
                  >
                     <button
                        type="button"
                        onClick={handleCloseInstallmentUpdateModal}
                        disabled={isSubmittingInstallmentUpdate}
                     >
                        Cancelar
                     </button>
                     <button
                        type="button"
                        onClick={handleInstallmentUpdateSubmit}
                        disabled={isSubmittingInstallmentUpdate}
                     >
                        {isSubmittingInstallmentUpdate
                           ? 'Salvando...'
                           : 'Salvar'}
                     </button>
                  </div>
               </Field>
            </FormGrid>
         </Modal>

         {/* ─── Modal: cancelar grupo ────────────────────────────────── */}
         <Modal
            isOpen={isCancellationModalOpen}
            title="Cancelar débito"
            onClose={handleCloseCancellationModal}
            maxWidth="440px"
         >
            <FormGrid $cols={12} $colsMobile={12}>
               <Field $col={12} $colMobile={12}>
                  <label htmlFor="detail-cancellation-reason">Motivo *</label>
                  <select
                     id="detail-cancellation-reason"
                     name="cancellation_reason"
                     value={cancellationFormData.cancellation_reason}
                     onChange={handleCancellationFieldChange}
                  >
                     <option value="">Selecione...</option>
                     <option value="CADASTRO_INCORRETO">
                        Cadastro incorreto
                     </option>
                     <option value="CANCELAMENTO_DE_DEBITO">
                        Cancelamento de débito
                     </option>
                  </select>
               </Field>

               <Field $col={12} $colMobile={12}>
                  <label htmlFor="detail-cancellation-notes">Observação</label>
                  <input
                     id="detail-cancellation-notes"
                     type="text"
                     name="cancellation_notes"
                     value={cancellationFormData.cancellation_notes}
                     onChange={handleCancellationFieldChange}
                     placeholder="Observação opcional..."
                  />
               </Field>

               <Field $col={12} $colMobile={12}>
                  <div
                     style={{
                        display: 'flex',
                        justifyContent: 'flex-end',
                        gap: '10px',
                        paddingTop: '4px',
                     }}
                  >
                     <button
                        type="button"
                        onClick={handleCloseCancellationModal}
                        disabled={isSubmittingCancellation}
                     >
                        Voltar
                     </button>
                     <button
                        type="button"
                        onClick={handleCancellationSubmit}
                        disabled={isSubmittingCancellation}
                     >
                        {isSubmittingCancellation
                           ? 'Cancelando...'
                           : 'Confirmar cancelamento'}
                     </button>
                  </div>
               </Field>
            </FormGrid>
         </Modal>
      </>
   );
}

DebtGroupDetailModal.propTypes = {
   isOpen: PropTypes.bool.isRequired,
   onClose: PropTypes.func.isRequired,
   onSuccess: PropTypes.func,
   debtType: PropTypes.oneOf(['ADIANTAMENTO', 'COMPRA_EM_FOLHA']).isRequired,
   employeeId: PropTypes.number.isRequired,
   employeeName: PropTypes.string,
   groupId: PropTypes.number.isRequired,
};
